import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { initEmailService } from './services/emailService';
import ScrollToTop from './components/ScrollToTop';

// Lazy-load all pages so the initial bundle only contains the shell
const Auth              = lazy(() => import('./components/Auth'));
const Landing           = lazy(() => import('./pages/Landing'));
const HomePage          = lazy(() => import('./pages/HomePage'));
const AboutPage         = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const FeaturesPage      = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const FAQPage           = lazy(() => import('./pages/FAQpages').then(m => ({ default: m.FAQPage })));
const ContactPage       = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const CreateWishlist    = lazy(() => import('./pages/CreateWishlist'));
const EditWishlist      = lazy(() => import('./pages/EditWishlist'));
const AddItem           = lazy(() => import('./pages/AddItem'));
const EditItem          = lazy(() => import('./pages/EditItem'));
const ViewWishlist      = lazy(() => import('./pages/ViewWishlist'));
const PublicWishlistView = lazy(() => import('./pages/PublicWishlistView'));
const AcceptInvitation  = lazy(() => import('./pages/AcceptInvitation'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const Settings          = lazy(() => import('./pages/Settings'));
const DebugCollaborators = lazy(() => import('./pages/DebugCollaborators'));
const CheckWishlist     = lazy(() => import('./pages/CheckWishlist'));
const MigrateCollaboratorIds = lazy(() => import('./pages/MigrateCollaboratorIds'));
const ShareHandler      = lazy(() => import('./pages/ShareHandler'));
const TestZaraProxy     = lazy(() => import('./components/TestZaraProxy'));

// Protected Route Component - Requires Authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#171717]' : 'bg-[#FCFCFA]'
      }`}>
        <div className="text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse ${
            theme === 'dark' ? 'bg-[#FCFCFA]/10' : 'bg-neutral-100'
          }`}>
            <div className={`w-8 h-8 rounded-lg opacity-20 ${
              theme === 'dark' ? 'bg-[#FCFCFA]' : 'bg-neutral-400'
            }`} />
          </div>
          <p className={theme === 'dark' ? 'text-neutral-400' : 'text-neutral-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" replace />;
};

// Public Route Component - Redirects to home if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#171717]' : 'bg-[#FCFCFA]'
      }`}>
        <div className="text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse ${
            theme === 'dark' ? 'bg-[#FCFCFA]/10' : 'bg-neutral-100'
          }`}>
            <div className={`w-8 h-8 rounded-lg opacity-20 ${
              theme === 'dark' ? 'bg-[#FCFCFA]' : 'bg-neutral-400'
            }`} />
          </div>
          <p className={theme === 'dark' ? 'text-neutral-400' : 'text-neutral-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/" replace /> : children;
};

// Root Route Component - Shows Landing for non-authenticated, HomePage for authenticated
const RootRoute = () => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#171717]' : 'bg-[#FCFCFA]'
      }`}>
        <div className="text-center">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse ${
            theme === 'dark' ? 'bg-[#FCFCFA]/10' : 'bg-neutral-100'
          }`}>
            <div className={`w-8 h-8 rounded-lg opacity-20 ${
              theme === 'dark' ? 'bg-[#FCFCFA]' : 'bg-neutral-400'
            }`} />
          </div>
          <p className={theme === 'dark' ? 'text-neutral-400' : 'text-neutral-600'}>Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <HomePage /> : <Landing />;
};

const PageFallback = () => {
  const { theme } = useTheme();
  return (
    <div className={`min-h-screen flex items-center justify-center ${
      theme === 'dark' ? 'bg-[#171717]' : 'bg-[#FCFCFA]'
    }`}>
      <div className="text-center">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse ${
          theme === 'dark' ? 'bg-[#FCFCFA]/10' : 'bg-neutral-100'
        }`}>
          <div className={`w-8 h-8 rounded-lg opacity-20 ${
            theme === 'dark' ? 'bg-[#FCFCFA]' : 'bg-neutral-400'
          }`} />
        </div>
      </div>
    </div>
  );
};

// App Routes with Theme
const AppRoutes = () => {
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#171717]' : 'bg-[#FCFCFA]'}`}>
      <Suspense fallback={<PageFallback />}>
      <Routes>
            {/* Public Authentication Route */}
            <Route 
              path="/auth" 
              element={
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              } 
            />
            
            {/* Custom Password Reset Page */}
            <Route
              path="/auth/reset"
              element={<ResetPasswordPage />}
            />

            {/* Web Share Target Handler - Handles shares from Safari/iOS */}
            <Route
              path="/share"
              element={<ShareHandler />}
            />

            {/* Test Zara Image Proxy */}
            <Route
              path="/test-proxy"
              element={<TestZaraProxy />}
            />

            {/* Public Wishlist Viewer - No Authentication Required */}
            <Route
              path="/shared/:shareToken"
              element={<PublicWishlistView />}
            />

            {/* Public Pages - No Authentication Required */}
            <Route
              path="/about"
              element={<AboutPage />}
            />
            <Route
              path="/features"
              element={<FeaturesPage />}
            />
            <Route
              path="/faq"
              element={<FAQPage />}
            />
            <Route
              path="/contact"
              element={<ContactPage />}
            />

            {/* Invitation Acceptance - Requires Authentication */}
            <Route
              path="/invite/:invitationToken"
              element={<AcceptInvitation />}
            />
            
            {/* Root Route - Landing for non-authenticated, HomePage for authenticated */}
            <Route
              path="/"
              element={<RootRoute />}
            />
            <Route 
              path="/create" 
              element={
                <ProtectedRoute>
                  <CreateWishlist />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/edit/:id" 
              element={
                <ProtectedRoute>
                  <EditWishlist />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/wishlist/:id/add-item"
              element={
                <ProtectedRoute>
                  <AddItem />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist/:id/edit-item/:itemId"
              element={
                <ProtectedRoute>
                  <EditItem />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist/:id"
              element={
                <ProtectedRoute>
                  <ViewWishlist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/debug-collaborators"
              element={
                <ProtectedRoute>
                  <DebugCollaborators />
                </ProtectedRoute>
              }
            />
            <Route
              path="/check-wishlist"
              element={
                <ProtectedRoute>
                  <CheckWishlist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/migrate-collaborator-ids"
              element={
                <ProtectedRoute>
                  <MigrateCollaboratorIds />
                </ProtectedRoute>
              }
            />

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  // Initialize email service on app startup
  useEffect(() => {
    initEmailService();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;